// W2-1 — THE BOOKMARK LAWS, IN ONE PLACE SO THEY CAN BE CHECKED.
//
// The loop the owner described is: bottom of the open book, "Bookmark this page.", name it, save it,
// find it in My Room, tap it, land on THAT page of THAT book. Almost all of that was already on
// disk. What was NOT anywhere was a single answer to the one question the loop turns on:
//
//   **WHICH NUMBER IS A PAGE?**
//
// Three counting systems meet here and nothing held them together:
//   • the DATABASE stores `page` 1-indexed, exactly as the member saw it (migration 016 says so);
//   • the TEXT reader (`BookReaderModal`) counts from 0 and renders `page + 1`;
//   • the PAGE-IMAGE reader (`PageImageReader`) counts from 1 and renders `page`.
//
// That is four conversions across three components, each one previously an inline `+ 1` / `- 1` at
// its call site. An off-by-one there does not throw, does not fail a build and does not look wrong
// in a diff. It lands the member on page 46 of the book she marked at 47, which is the kind of
// small wrongness nobody reports and everybody feels. So the conversions live here, as pure
// functions a gate can RUN, and the round trip is asserted rather than assumed.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

export type Bookmark = {
  id: number;
  bookSlug: string;
  bookTitle: string;
  page: number;
  name: string;
  createdAt?: string;
  // M3: the book has left the library; the mark is kept and says so instead of opening nothing.
  gone?: boolean;
};

// HER WORDS, at the bottom of every open book. ONE string, both readers, so the text reader and the
// page-image reader can never come to offer the member two different invitations.
export const BOOKMARK_PROMPT = "Bookmark this page.";

// The naming step. The page number rides in the placeholder so she can see WHAT she is naming.
// 14-B7d(2): the sentence is a FALLBACK PARAMETER with the English as its default (this module
// imports nothing, so it cannot read the catalog), and the `{page}` token is filled HERE — the same
// number, in whichever language the sentence around it is written. `w1-bookmarks.mjs` calls it with
// one argument and reads the English back, which is why a required argument would be the wrong shape.
export const BOOKMARK_NAME_TEMPLATE = "Name this bookmark (page {page})";
export const bookmarkNamePlaceholder = (page: number, template: string = BOOKMARK_NAME_TEMPLATE) =>
  String(template || BOOKMARK_NAME_TEMPLATE).replace("{page}", String(page));

// ---------------------------------------------------------------------------------------------
// THE ROUTES. Held by the gate against the server's own handler, because a wrong URL here is a
// silent 404 on a fetch whose failure path is `.catch(() => {})` — no error, no bookmark, no clue.
// ---------------------------------------------------------------------------------------------
export const bookmarksUrl = (bookSlug?: string) =>
  bookSlug ? `/api/bookmarks?slug=${encodeURIComponent(bookSlug)}` : "/api/bookmarks";

export const bookmarkUrl = (id: number) => `/api/bookmarks/${id}`;

// ---------------------------------------------------------------------------------------------
// THE COUNTING. `page` in a bookmark is ALWAYS what the member read on the screen: 1-indexed.
// ---------------------------------------------------------------------------------------------

// Clamp a 1-indexed page into a book that has `totalPages` of them. A book still loading reports
// zero pages; landing on 1 is the only honest answer there, never 0 and never a negative index.
export const clampBookmarkPage = (page: number, totalPages: number) => {
  const last = Math.max(1, Math.floor(totalPages) || 1);
  const want = Math.floor(Number(page)) || 1;
  return Math.min(last, Math.max(1, want));
};

// A saved bookmark opening the TEXT reader, which counts from zero.
export const toReaderIndex = (page: number, totalPages: number) => clampBookmarkPage(page, totalPages) - 1;

// The text reader's own index, on its way back out to a bookmark or to the page counter.
export const toBookmarkPage = (readerIndex: number) => Math.max(1, (Math.floor(Number(readerIndex)) || 0) + 1);

// A saved bookmark opening the PAGE-IMAGE reader, which already counts from one.
export const toImagePage = (page: number, totalPages: number) => clampBookmarkPage(page, totalPages);

// What the My Room list says under the member's own name for the mark. Her name leads; this is the
// line that tells her which book and where, and it is the same shape for both kinds of reader.
export const bookmarkWhere = (bookmark: Pick<Bookmark, "bookTitle" | "bookSlug" | "page">) =>
  `${bookmark.bookTitle || bookmark.bookSlug} · p.${Math.max(1, Math.floor(Number(bookmark.page)) || 1)}`;

// R59 — THE READ. Both call sites (My Room's `reloadBookmarks` and the reader's own `load`) wrote
// `r.ok ? r.json() : { bookmarks: [] }` and then `Array.isArray(d.bookmarks) ? d.bookmarks : []`, so
// a failed request and a member with no bookmarks were the same answer — and what a bookmark holds
// is the page she stopped on. **AN EMPTY LIST IS HER PLACE LOST**, and the list it replaced was
// correct a second earlier.
//
// null means the read did not land; the surface keeps whatever it already had. `doFetch` is a
// PARAMETER (R46) so a gate drives every answer without a browser or a database.
export async function listBookmarks<T>(bookSlug?: string, doFetch: typeof fetch = fetch): Promise<T[] | null> {
  try {
    const res = await doFetch(bookmarksUrl(bookSlug), { cache: "no-store" });
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { bookmarks?: unknown } | null;
    return Array.isArray(data?.bookmarks) ? (data.bookmarks as T[]) : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------------------------
// R61 — THE WRITES. R59 fixed both READS on this route and left these two named in the RUNBOOK.
//
// **SHE NAMES A BOOKMARK, TAPS SAVE, AND IS TOLD NOTHING WHEN IT DOES NOT GO.** The save was
// `await fetch(...).catch(() => {})` — not the payload, not even the status code — followed by
// `setName(""); setNaming(false); load();`. The form closes and the list re-reads, so the SCREEN
// ends up honest, and that is exactly what makes it invisible: **an absent chip among a row of
// chips she already has is not something anybody notices** (R25's argument — an absent control has
// no error state — applied to an absent RESULT). She goes back to that page a week later expecting
// her mark, and this route refuses FIVE ways, all of them silent:
//
//   * **401** — "Sign in to bookmark." Her session expired mid-book; the one refusal she could fix
//     in one tap, and the one she was never told about.
//   * **400** — "A bookmark needs a book and a name."
//   * **HTTP 200 with `{ ok: false }`** when `ensureUser` comes back empty (R39/R47/R51/R53/R54/
//     R55/R60's shape, and R59 put it on this very handler while fixing the read beside it).
//   * **500** — "Bookmarks are unavailable right now."
//   * **A 200 CARRYING NO `bookmark`** — the row comes back in the shape the route returns, so an
//     absent one is a malformed answer and nothing was saved (R18's seal-status lesson, R53's
//     `data` guard).
//
// The DELETE is the same silence one act over, and R35 already ruled it cheap enough not to ASK
// (`NO_ASK_BY_DESIGN`: "a page bookmark in the library reader — re-made by turning to the page
// again"). **That ruling is about the CONFIRM, never about the ANSWER**, and the server has always
// sent one — `sendJson(response, ok ? 200 : 404, { ok })`, a real boolean off a real `rowCount`,
// thrown away by `.catch(() => {})`. It is R54's finding on the verse unpin word for word.
//
// **THE RE-READ AFTER EACH WRITE STAYS AND IS THE REASON THE SENTENCE IS ENOUGH.** The list is
// served by the server, so re-reading is what makes the screen agree with the database (R54's
// put-back ruling); what was missing was never the correction, it was being told. And if the
// re-read itself fails it answers null and the list keeps what it had (R59), which is right twice.
//
// `doFetch` is a PARAMETER (R46) so a gate drives every one of those answers without a browser, a
// session or a database.
// ---------------------------------------------------------------------------------------------

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart. The shape
// is deliberately `saved-reports.ts`/`card-library.ts`'s — a third shape for the same four-way
// question is a third answer key (R51's ruling, applied since).
export type BookmarkAnswer =
  | { ok: true }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

type BookmarkJson = { ok?: boolean; message?: string; bookmark?: unknown };

const refusal = (data: BookmarkJson): BookmarkAnswer =>
  ({ ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" });

export async function saveBookmark(
  input: { bookSlug: string; bookTitle: string; page: number; name: string },
  doFetch: typeof fetch = fetch,
): Promise<BookmarkAnswer> {
  try {
    const res = await doFetch(bookmarksUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as BookmarkJson;
    // **THE SAVED ROW IS THE PROOF.** `{ ok: true }` with no bookmark on it is not a save.
    if (res.ok && data?.ok !== false && data?.bookmark) return { ok: true };
    return refusal(data);
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// R35's `NO_ASK_BY_DESIGN` covers the CONFIRM, never the ANSWER. The 404 half is load-bearing: it is
// what the server says when the id was not hers, and it arrives with `{ ok: false }` rather than a
// sentence — so the house's own line is what she reads, which is why `copy.failed` is not optional.
export async function deleteBookmark(id: number, doFetch: typeof fetch = fetch): Promise<BookmarkAnswer> {
  try {
    const res = await doFetch(bookmarkUrl(id), { method: "DELETE" });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as BookmarkJson;
    if (res.ok && data?.ok !== false) return { ok: true };
    return refusal(data);
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// The sentence, derived from the answer rather than typed per call site. **A SUCCESS SAYS NOTHING**
// — the chip appearing (or leaving) IS the answer, and a "Saved" line under every bookmark she
// names is noise (R51/R53/R54/R55/R57/R58/R60 on the same question). The server's own words are
// preferred where it sent any, because only the server knows which of the five refusals this was.
export type BookmarkCopy = { signIn: string; failed: string };

export function bookmarkNote(answer: BookmarkAnswer, copy: BookmarkCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
